import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Calendar,
  Clock,
  Share2,
  ArrowRight,
  Star,
  Newspaper,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const calculateReadingTime = (text) => {
  if (!text) return "1 min read";
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${Math.max(1, minutes)} min read`;
};

const isFeaturedArticle = (item) => {
  if (!item) return false;
  return (
    item.featured === true ||
    item.featured === 1 ||
    item.featured === "1" ||
    item.featured === "true" ||
    item.isFeatured === true ||
    item.isFeatured === 1 ||
    item.isFeatured === "1" ||
    item.isFeatured === "true"
  );
};

const PublicNewsCard = ({
  article,
  viewMode = "grid",
  onRead,
  onShare,
  formatTimeAgo,
  formatPublishDate,
}) => {
  if (!article) return null;

  const {
    title,
    description,
    imageUrl,
    updatedOn,
    createdOn,
  } = article;

  const snippet = stripHtml(description);
  const displayDate = updatedOn || createdOn;
  const readingTime = calculateReadingTime(snippet);
  const isFeatured = isFeaturedArticle(article);

  // LIST VIEW LAYOUT
  if (viewMode === "list") {
    return (
      <Card
        className="public-news-card"
        sx={{
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          overflow: "hidden",
          mb: 2.5,
          transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: customShadows.cardHover,
            borderColor: colors.slate[300],
          },
        }}
      >
        <Grid container alignItems="stretch">
          {/* Left: Thumbnail Image */}
          <Grid item xs={12} sm={4} md={3.5}>
            <Box
              sx={{
                width: "100%",
                height: { xs: 200, sm: "100%" },
                minHeight: { sm: 180 },
                position: "relative",
                overflow: "hidden",
                bgcolor: colors.slate[100],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={title}
                  className="news-card-image"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.35s ease",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: colors.slate[400],
                  }}
                >
                  <Newspaper size={32} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Official News
                  </Typography>
                </Box>
              )}

              {isFeatured && (
                <Box sx={{ position: "absolute", top: 12, left: 12 }}>
                  <CommonChip
                    label="Featured"
                    startIcon={<Star size={11} fill={colors.amber[500]} color={colors.amber[500]} />}
                    size="small"
                    sx={{
                      bgcolor: colors.neutral.white,
                      color: colors.amber[800],
                      fontWeight: 700,
                      boxShadow: customShadows.card,
                      border: `1px solid ${colors.amber[200]}`,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right: Article Details */}
          <Grid item xs={12} sm={8} md={8.5}>
            <Box
              sx={{
                p: { xs: 2.5, sm: 3 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <Box>
                {/* Meta Header */}
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1, gap: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
                    <Calendar size={12} color={colors.slate[400]} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatPublishDate(displayDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
                    <Clock size={12} color={colors.slate[400]} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatTimeAgo(displayDate)}
                    </Typography>
                  </Box>

                  <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                    • {readingTime}
                  </Typography>
                </Stack>

                {/* Title */}
                <Typography
                  variant="h6"
                  onClick={() => onRead(article)}
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    lineHeight: 1.35,
                    fontSize: "1.1rem",
                    mb: 1,
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: colors.primary.main,
                    },
                  }}
                >
                  {title}
                </Typography>

                {/* Excerpt */}
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.slate[600],
                    lineHeight: 1.6,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  {snippet}
                </Typography>
              </Box>

              {/* Bottom Actions */}
              <Box
                sx={{
                  pt: 1.5,
                  borderTop: `1px solid ${colors.dashboard.tableBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <CommonButton
                  variant="primary"
                  size="small"
                  endIcon={<ArrowRight size={14} />}
                  onClick={() => onRead(article)}
                >
                  Read Article
                </CommonButton>

                <Tooltip title="Share link">
                  <IconButton
                    size="small"
                    onClick={() => onShare(article)}
                    sx={{
                      bgcolor: colors.slate[50],
                      color: colors.slate[600],
                      border: `1px solid ${colors.slate[200]}`,
                      "&:hover": {
                        bgcolor: colors.blue[50],
                        color: colors.primary.main,
                        borderColor: colors.blue[200],
                      },
                    }}
                  >
                    <Share2 size={15} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
    );
  }

  // GRID VIEW LAYOUT (Default)
  return (
    <Card
      className="public-news-card"
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: customShadows.cardHover,
          borderColor: colors.slate[300],
        },
      }}
    >
      {/* Cover Image */}
      <Box
        sx={{
          width: "100%",
          height: 190,
          position: "relative",
          overflow: "hidden",
          bgcolor: colors.slate[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            className="news-card-image"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.35s ease",
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: colors.slate[400],
            }}
          >
            <Newspaper size={36} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Autobet News
            </Typography>
          </Box>
        )}

        {isFeatured && (
          <Box sx={{ position: "absolute", top: 12, left: 12 }}>
            <CommonChip
              label="Featured"
              startIcon={<Star size={11} fill={colors.amber[500]} color={colors.amber[500]} />}
              size="small"
              sx={{
                bgcolor: colors.neutral.white,
                color: colors.amber[800],
                fontWeight: 700,
                boxShadow: customShadows.card,
                border: `1px solid ${colors.amber[200]}`,
              }}
            />
          </Box>
        )}
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          justifyContent: "space-between",
        }}
      >
        <Box>
          {/* Metadata Row */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
              <Calendar size={12} color={colors.slate[400]} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatPublishDate(displayDate)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
              <Clock size={12} color={colors.slate[400]} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatTimeAgo(displayDate)}
              </Typography>
            </Box>
          </Stack>

          {/* Headline Title */}
          <Typography
            variant="h6"
            onClick={() => onRead(article)}
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              lineHeight: 1.35,
              fontSize: "1.05rem",
              mb: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              cursor: "pointer",
              transition: "color 0.2s ease",
              "&:hover": {
                color: colors.primary.main,
              },
            }}
          >
            {title}
          </Typography>

          {/* Snippet */}
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[600],
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 2,
            }}
          >
            {snippet}
          </Typography>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            pt: 2,
            borderTop: `1px solid ${colors.dashboard.tableBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="caption" sx={{ color: colors.slate[400], fontWeight: 500 }}>
            {readingTime}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Share article link">
              <IconButton
                size="small"
                onClick={() => onShare(article)}
                sx={{
                  bgcolor: colors.slate[50],
                  color: colors.slate[600],
                  border: `1px solid ${colors.slate[200]}`,
                  "&:hover": {
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    borderColor: colors.blue[200],
                  },
                }}
              >
                <Share2 size={14} />
              </IconButton>
            </Tooltip>

            <CommonButton
              variant="primary"
              size="small"
              endIcon={<ArrowRight size={13} />}
              onClick={() => onRead(article)}
            >
              Read
            </CommonButton>
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

PublicNewsCard.propTypes = {
  article: PropTypes.object.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]),
  onRead: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  formatTimeAgo: PropTypes.func.isRequired,
  formatPublishDate: PropTypes.func.isRequired,
};

export default PublicNewsCard;
